import type { Anrede } from "./anrede.js";

/**
 * Neutrale Rollen in der Reihenfolge von UL §9 (Mehrfachrollen werden in dieser Reihenfolge genannt,
 * oberflaeche.md §2.1). Gespeichert wird immer die neutrale Rolle, die Bezeichnung entsteht bei der Anzeige (ADR-0025).
 */
export const ROLLEN = [
  "Jagdherr", "Jagdleiter", "Schütze", "Ansteller", "Treiber", "Treiberführer", "Durchgeh-Hundeführer",
  "Standschnaller", "Nachsuchenführer", "Nachweisprüfer", "Jagdgast", "Bergetrupp", "Wildversorgung", "Erleger", "Teilnehmer",
] as const;
export type Rolle = (typeof ROLLEN)[number];

const WEIBLICH: Partial<Record<Rolle, string>> = {
  Jagdherr: "Jagdherrin", Jagdleiter: "Jagdleiterin", Schütze: "Schützin", Ansteller: "Anstellerin", Treiber: "Treiberin",
  Treiberführer: "Treiberführerin", "Durchgeh-Hundeführer": "Durchgeh-Hundeführerin", Standschnaller: "Standschnallerin",
  Nachsuchenführer: "Nachsuchenführerin", Nachweisprüfer: "Nachweisprüferin", Erleger: "Erlegerin", Teilnehmer: "Teilnehmerin",
};

/** Rollen ohne geschlechtliche Form (oberflaeche.md §2.1 „alle“). */
const NEUTRAL: Partial<Record<Rolle, string>> = {
  Jagdgast: "Jagdgast", Bergetrupp: "Mitglied des Bergetrupps", Wildversorgung: "Wildversorgung",
};

/** Symbol je Rolle (design/README §8.3, oberflaeche.md §1.4). */
export const ROLLENSYMBOL: Record<Rolle, string> = {
  Jagdherr: "jagdherr", Jagdleiter: "jagdleiter", Schütze: "schuetze", Ansteller: "ansteller", Treiber: "treiber",
  Treiberführer: "treiberfuehrer", "Durchgeh-Hundeführer": "hundefuehrer", Standschnaller: "hundefuehrer",
  Nachsuchenführer: "hundefuehrer", Nachweisprüfer: "nachweispruefer", Jagdgast: "teilnehmer", Bergetrupp: "bergung",
  Wildversorgung: "wildversorgung", Erleger: "strecke", Teilnehmer: "teilnehmer",
};

export function sortiereRollen(rollen: readonly Rolle[]): Rolle[] {
  return [...rollen].sort((a, b) => ROLLEN.indexOf(a) - ROLLEN.indexOf(b));
}

/**
 * Rollenbezeichnung nach Anrede (Wertobjekt `Rollenbezeichnung`, ADR-0025). Ohne Anrede gibt es keine
 * Personenbezeichnung: dann `null`, und die Anzeige nimmt Symbol und Beschreibung (oberflaeche.md §2.2).
 */
export function rollenbezeichnung(rolle: Rolle, anrede: Anrede): string | null {
  const neutral = NEUTRAL[rolle];
  if (neutral) return neutral;
  if (anrede === "ohne Anrede") return null;
  return anrede === "Frau" ? (WEIBLICH[rolle] ?? rolle) : rolle;
}

/** Mehrere Rollen, mit `, ` verbunden in der Reihenfolge von UL §9; ohne Anrede `null`. */
export function rollenbezeichnungen(rollen: readonly Rolle[], anrede: Anrede): string | null {
  const teile = sortiereRollen(rollen).map((r) => rollenbezeichnung(r, anrede));
  if (teile.some((t) => t === null)) return null;
  return teile.join(", ");
}

/** Bezüge, die eine Beschreibung ohne Anrede braucht (Stand, Gruppe …). */
export interface Rollenbezug {
  stand?: string;
  anstellgruppe?: string;
  treibergruppe?: string;
}

/** Beschreibung ohne Anrede, wörtlich nach oberflaeche.md §2.2. */
export function beschreibungOhneAnrede(rolle: Rolle, bezug: Rollenbezug = {}): string {
  switch (rolle) {
    case "Jagdherr": return "verwaltet das Revier";
    case "Jagdleiter": return "leitet die Jagd";
    case "Schütze": return bezug.stand ? `Stand ${bezug.stand}` : "Gast";
    case "Jagdgast": return "Gast";
    case "Ansteller": return bezug.anstellgruppe ? `stellt Gruppe ${bezug.anstellgruppe} an` : "stellt an";
    case "Treiber": return bezug.treibergruppe ? `Treibergruppe ${bezug.treibergruppe}` : "Treibergruppe";
    case "Treiberführer": return bezug.treibergruppe ? `führt Treibergruppe ${bezug.treibergruppe}` : "führt Treibergruppe";
    case "Durchgeh-Hundeführer": return "geht mit Hund durch";
    case "Standschnaller": return bezug.stand ? `schnallt am Stand ${bezug.stand}` : "führt Hund";
    case "Nachsuchenführer": return "Nachsuchengespann";
    case "Nachweisprüfer": return "prüft Papiere";
    case "Erleger": return bezug.stand ? `erlegt von Stand ${bezug.stand}` : "erlegt";
    case "Bergetrupp": return "Mitglied des Bergetrupps";
    case "Wildversorgung": return "Wildversorgung";
    case "Teilnehmer": return "Gast";
  }
}

/** Fließtext: `<Rollenbezeichnung> <Name>` bzw. ohne Anrede `<Name>, <Beschreibung>` (oberflaeche.md §2.1, §2.2). */
export function personImText(name: string, rolle: Rolle, anrede: Anrede, bezug: Rollenbezug = {}): string {
  const b = rollenbezeichnung(rolle, anrede);
  return b ? `${b} ${name}` : `${name}, ${beschreibungOhneAnrede(rolle, bezug)}`;
}
